import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";
import { srcsetOf } from "@common/schema/site-file/sizes";

// A Quill delta, as stored, to the HTML a page shows.
//
// This is the one piece of the paragraph port that had to match gen-2 EXACTLY,
// because the content it renders was written there: three of www.oatblog.com's
// pages carry a paragraph element, and the migration is only lossless if the
// same delta produces the same markup. So it is the same library at the same
// major (`quill-delta-to-html` 0.12) with the same one option that changes what
// it emits, which is what `libs/site-render/components/paragraph` in the
// archived project did. The second option below changes nothing for content
// that was safe already — see the note on it.
//
// `multiLineParagraph: true` keeps consecutive newlines inside one <p> with
// <br/> between them instead of opening a paragraph per line. Quill's own
// editor shows the first; dropping the option silently re-flows every
// paragraph ever written.

// WHICH URLS A LINK MAY POINT AT
//
// A paragraph is the first field in this app whose content is PASTED as often
// as it is typed: Quill turns pasted HTML into a delta, so bytes from Word, a
// website or another CMS arrive as ops the author never consciously wrote and
// would not recognise as markup. Nothing about the principal changes — there is
// no role separation here, and anyone who can write a paragraph could already
// write a `code` element's script — but a control that looks like a text box
// invites content whose origin nobody vetted, and an `href` is where that lands.
//
// The library already refuses `javascript:` (it prefixes `unsafe:`, which
// resolves as a relative path and goes nowhere). This narrows the rest of its
// list — `file:`, `blob:`, `s?ftp:`, `data:image/` — to the four schemes prose
// actually links with, because the editor's preview frame is same-origin with
// the app today (P142) and the anchor guard in preview/mount.js covers only the
// anchors present when it runs.
//
// Returning `undefined` for an allowed link is not laziness: it is what makes
// the option free of consequences for everything else. The converter falls
// through to its own `encodeLink(sanitize(...))` for an undefined result, so
// every URL that survives is encoded exactly as it was before this existed —
// which is what keeps the gen-2 output below byte-for-byte identical.
const ALLOWED_SCHEMES = new Set(["http", "https", "mailto", "tel"]);

// Control characters and leading space are stripped before the scheme is read,
// because a browser strips them too — `java\nscript:` is a scheme to a browser
// and would not be one to a naive regex.
const SCHEME = /^([a-zA-Z][a-zA-Z0-9+.\-]*):/;

const urlSanitizer = (link) => {
  const bare = String(link).replace(/[\u0000-\u0020]/g, "");
  const scheme = SCHEME.exec(bare);

  // No scheme at all is a relative URL — `/pricing`, `#top`, `../about` — and
  // those are the ones a page links to most.
  if (!scheme) {
    return undefined;
  }

  // "" is how this hook says "no link". The converter treats an empty result as
  // an op with no `link` attribute at all, so the anchor is not emitted and the
  // words the author wrote are still on the page as text — which is the right
  // outcome: a refused href should cost the link, not the sentence.
  return ALLOWED_SCHEMES.has(scheme[1].toLowerCase()) ? undefined : "";
};

// THE `minimap` EMBED — an image, put on a page from the site's own files.
//
// gen-2's custom blot, and the reason the name is what it is: there, the file
// selector could hand back either the original or a generated thumbnail, and
// the blot was named after the thumbnail. Only the name survives — this one
// carries a URL and nothing else. It is kept rather than renamed because the
// delta is STORED, and three of www.oatblog.com's paragraphs came from gen-2:
// a rename would be a data migration to buy a better word.
//
// What can reach here is `{ insert: { minimap: { url } }, attributes }`, and
// every part of that was written by a browser — so none of it is trusted. The
// converter escapes `&`, `<`, `>` and `"` in TEXT; it does nothing at all to a
// custom op, because the whole point of `renderCustomWith` is that the markup
// is yours. This function is therefore the only thing between a stored string
// and an `<img>` tag on a served page.
//
// THE URL
//
// `imageSrc` below, and not `urlSanitizer`. A link and an image src are not the
// same question: `mailto:` and `tel:` are fine on an anchor and meaningless on
// an image, and `data:` is the one an image invites — a `data:image/svg+xml`
// carries a `<script>`, which is exactly what bucketws' own storage rules
// refuse to serve inline (`safeContentHeaders`, and the note in
// @common/schema/site-file/kind on why svg is not previewable). So the answer
// is narrower than the anchor's: relative, http, https.
//
// A URL that does not survive drops the whole embed rather than emitting an
// `<img>` with an empty src, which would render as a broken-image icon and read
// as "the file was deleted".
//
// THE ATTRIBUTES
//
// `alt`, `width` and `height` are rendered; `style` is NOT, and that is a
// deliberate difference from gen-2. gen-2 sized images with
// `quill-image-resize-module`, which writes an inline `style` string, and the
// renderer interpolated it raw — a stored string spliced into a style attribute
// on a page. That module is not ported and nothing this app writes sets any of
// the three, so the only content that can carry them is migrated gen-2 content;
// `width` and `height` are constrained to a number or a percentage, `alt` is
// escaped, and a `style` is dropped. If image resizing is ever wanted, it is a
// width and not a stylesheet.
const NUMERIC = /^\d{1,5}(?:\.\d{1,3})?%?$/;

const ALLOWED_IMAGE_SCHEMES = new Set(["http", "https"]);

const imageSrc = (url) => {
  if (typeof url !== "string" || url.trim() === "") {
    return null;
  }

  const bare = url.replace(/[\u0000-\u0020]/g, "");
  const scheme = SCHEME.exec(bare);

  // No scheme is a relative URL, which is what every image picked from this
  // site's own files is: `/file/<kind>/<fileId>/content/<name>`. Relative
  // because the host is inserted by whichever verified domain the visitor
  // arrived on — see @common/schema/site-file/url — so attaching a second
  // domain, detaching one or swapping them changes nothing that was written
  // into a paragraph.
  //
  // That relative form is also why the composer takes a BASE rather than a
  // rewritten value: this renderer emits exactly what was stored, and
  // `bodyOf` rebases the assembled body for whichever context is about to read
  // it — identity on a tenant hostname, `/file/by-site/<orgId>/<siteId>/` in
  // the editor's preview and on the distribution's own name. So nothing here
  // needs to know who is reading, which is the property worth keeping if this
  // renderer is ever changed (docs/pagews-files.md §7b).
  //
  // `//evil.example/x` is protocol-relative and has no scheme, so it is checked
  // for separately rather than being let through as "relative".
  if (!scheme) {
    return bare.startsWith("//") ? null : bare;
  }

  return ALLOWED_IMAGE_SCHEMES.has(scheme[1].toLowerCase()) ? bare : null;
};

const sizeAttr = (name, value) =>
  typeof value === "string" && NUMERIC.test(value) ? ` ${name}="${value}"` : "";

// WHICH SIZE THE VISITOR'S BROWSER FETCHES
//
// bucketws derives a ladder of widths for every raster it stores and serves
// one of them for `?width=` — @common/schema/site-file/sizes has the whole
// arrangement. A paragraph image used to name only the original, so a phone
// on a slow connection pulled the same 4000-pixel photograph as a desktop.
// It now carries a `srcset` of the derived widths and lets the browser choose,
// which is the one mechanism for this that needs no script on the page.
//
// `sizes` is the half the browser cannot work out for itself, and a delta
// does not know the page's layout: the image is a block inside whatever
// column the tenant's template gives it. Nothing is emitted unless the op
// carries a width of its own — a migrated gen-2 op can — and the browser then
// assumes the full viewport, which errs towards the sharper picture. A pixel
// width is what it says; a percentage is read as a share of the viewport,
// which is what it was of the editor's column when it was written.
//
// Only the ops whose `url` is a site file get any of this. An absolute URL is
// somebody else's image and is emitted as it was; the tests pin that the
// markup for one is byte-for-byte what it was before this existed.
const sizesAttr = (width) => {
  if (typeof width !== "string" || !NUMERIC.test(width)) {
    return "";
  }

  return width.endsWith("%")
    ? ` sizes="${width.slice(0, -1)}vw"`
    : ` sizes="${width}px"`;
};

const srcsetAttr = (url, sourceWidth) => {
  const srcset = srcsetOf(url, sourceWidth);

  return srcset ? ` srcset="${escapeHtml(srcset)}"` : "";
};

const renderCustom = (customOp) => {
  const { insert, attributes } = customOp || {};

  // Every other custom type is somebody else's, and returning undefined is how
  // this hook says "not mine" — the converter then emits nothing for the op
  // rather than throwing.
  if (!insert || insert.type !== "minimap") {
    return undefined;
  }

  const value = insert.value || {};
  const src = imageSrc(value.url);

  if (!src) {
    return "";
  }

  const attrs = attributes || {};

  // `alt` is ALWAYS emitted, empty string and all, and that is the difference
  // between two things that look the same. An <img> with no alt attribute is
  // announced by a screen reader as its filename or its URL — here a uuid;
  // `alt=""` says "this picture carries no information", which is the honest
  // answer for one nobody described.
  const alt = attrs.alt || value.description || "";

  // The source's own pixel width, when the picker knew it. Untrusted like the
  // rest of the value: anything that is not a positive number reads as unknown.
  const sourceWidth = Number(value.sourceWidth);
  const srcset = srcsetAttr(src, sourceWidth > 0 ? sourceWidth : undefined);

  return `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"${srcset}${
    srcset ? sizesAttr(attrs.width) : ""
  }${sizeAttr("width", attrs.width)}${sizeAttr("height", attrs.height)} />`;
};

const OPTIONS = { multiLineParagraph: true, urlSanitizer };

// Errors are content, not exceptions — the same rule the rest of the composer
// follows.
//
// `content` is a string a person's browser wrote and a person's browser can
// truncate: a save that raced a reload leaves half a delta in the row, and
// JSON.parse throws on it. Throwing here would take the whole page down —
// including the header and footer, which have nothing to do with this element
// — so a broken paragraph renders as a broken paragraph and everything around
// it still serves.
//
// The message is ESCAPED, and that is not tidiness. V8 quotes the offending
// input back in a JSON parse error — `Unexpected token 'n', "{not json" is not
// valid JSON` — so an unescaped message is a path by which the stored string
// reaches the page as markup, on exactly the content this module exists to
// render safely. The rest of the composer's error strings carry the same shape
// and predate this; this one does not add to them.
const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const ERROR = (message) =>
  `<div style="color:#b00;font:12px monospace">Paragraph error: ${escapeHtml(
    message
  )}</div>`;

// The line that makes "a paragraph is never templated" TRUE rather than nearly
// true, and it took a second reader to find the gap.
//
// Bypassing `Template.render` removes the EJS pass, and that much was right.
// What it does not remove is the OTHER templating pass: `bodyOf` in ./page ends
// with `Template.interpolate(composed, context)` over the ASSEMBLED body, so
// every element's output goes through `{{...}}` substitution after the fact —
// on both the served page and the editor's preview, which call the same
// function. `quill-delta-to-html` escapes `&`, `<`, `>` and `"`; it does not
// escape braces. So `{{page.title}}` typed as a sentence survived the converter
// intact and was substituted RAW at compose time.
//
// Two things were wrong with that, and the smaller one is the security one.
// Splicing a context value is the same trust level as everything else on the
// page — one organization's own content — but it also means a run of braces in
// prose silently DISAPPEARS, because interpolate replaces an unresolved name
// with the empty string. www.oatblog.com is a blog about a page service; a post
// explaining `{{...}}` syntax would have been quietly emptied by the thing it
// was explaining.
//
// `{` only: `interpolate`'s pattern needs `{{` to match, so breaking the open
// brace is enough and leaves `}` alone. It cannot regress the gen-2 fidelity
// above unless a migrated paragraph contains a brace, and none of the ones on
// www.oatblog.com does — checked against the rows, elements AND params alike,
// not assumed.
//
// Deliberately not a count. This said "none of the three" when the only
// paragraphs were the three ELEMENTS; the param type went live the same day and
// the number was wrong by the afternoon. A comment that has to be recounted
// every time somebody migrates a row is a comment that will be wrong most of
// the time, and the claim does not need the number to be true.
//
// THIS STAYS EVEN WHEN `interpolate` STOPS RAW-SPLICING. There is work in
// flight to fix that pass properly, and when it lands this line stops being
// load-bearing and starts looking like dead weight — which is the moment
// somebody deletes it. It is defence in depth and not the primary control: a
// per-type escape that survives a change to the shared pass is exactly what you
// want when the shared pass is the thing that keeps regressing, and this type's
// whole promise is that what an author typed is what a reader sees.
const braces = (html) => html.replace(/{/g, "&#123;");

export const render = (content) => {
  if (!content) {
    return "";
  }

  let ops;

  try {
    ({ ops } = JSON.parse(content));
  } catch (e) {
    // Through `braces` like everything else: the parse error quotes the input
    // back, so the stored string reaches the page here too.
    return braces(ERROR(e.toString()));
  }

  // A delta is `{ops: [...]}`. Anything else — `null`, a bare array, an object
  // from some other editor — is not one, and the converter's own failure for
  // those is a TypeError deep inside it rather than a message anyone can act
  // on.
  if (!Array.isArray(ops)) {
    return ERROR("not a delta");
  }

  try {
    const converter = new QuillDeltaToHtmlConverter(ops, OPTIONS);

    // A method rather than an option: this is how 0.12 takes a custom-blot
    // renderer, and it is the same call gen-2's `libs/site-render/components/
    // paragraph` made. It has to be made before convert().
    converter.renderCustomWith(renderCustom);

    return braces(converter.convert());
  } catch (e) {
    return braces(ERROR(e.toString()));
  }
};

// The delta's plain text, for the places that must not render its markup.
//
// A list row and a read-only form live on the app's own origin, and tenant
// markup does not go there (P142) — so the widget's View shows this instead.
// Kept beside render() rather than in the widget because "what is inside a
// delta" is knowledge of the format, and the format is this module's subject.
//
// Embeds (`insert` is an object, not a string) contribute nothing: there is no
// text in an image or a video, and the alternative — a placeholder word — would
// read as content the author did not write.
export const text = (content) => {
  if (!content) {
    return "";
  }

  let ops;

  try {
    ({ ops } = JSON.parse(content));
  } catch (e) {
    return "";
  }

  if (!Array.isArray(ops)) {
    return "";
  }

  return ops
    .map(({ insert }) => (typeof insert === "string" ? insert : ""))
    .join("")
    .trim();
};
